/* eslint-disable react/no-array-index-key */
import { addCreditCardTE, CreditCardPayload, getCreditCardsTE } from 'api/users/creditCards';
import { dialogComponents } from 'components/Dialog/dialogComponents';
import { Button } from 'components/Button';
import { checkIfHasAllScopes, ScopeMatcher, useCurrentUserId, useUserLevelScopes } from 'modules/Auth/hooks';
import { createContext, useCallback, useContext, useState } from 'react';
import { either, errorToReactLeft, noop, pipe } from 'utils/fp';
import { queryKeys, useMutationTE, useQueryTE } from 'utils/react-query';
import { cx } from '@linaria/core';
import { IconCheckThin, IconLinkExternal } from 'components/Icons';
import { useBoolean } from 'react-use';
import { CreditCardFormSubmitCallback, CreditCardModal } from 'modules/User/components/Settings/Billing/CreditCard/CreditCardModal';
import { useQueryClient } from 'react-query';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { useUserSubscriptionMutation } from 'modules/User/components/Settings/Billing/hooks/useUserSubscriptionMutation';
import { billingApiEffects } from 'modules/User/components/Settings/Billing/V2Api';
import { BillingPlanIconMapping, Plan, TBillingPlansDataLD, Text } from 'modules/Home/constants';
import { ENTERPRISE_PLAN_ID } from 'modules/Organizations/constants';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { Loader, LoadingOverlay } from '@mantine/core';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { AuthModalState } from 'modules/Auth/components/AuthModal';
import { useAuthActions } from 'modules/Auth/AuthContext';
import { useFeaturedFlags } from 'modules/FeaturedFlags/useFeaturedFlags';
import { FeaturedFlagsUnion } from 'modules/FeaturedFlags/FeaturedFlagsContext';
import { useRouter } from 'next/router';
import { titleStyles, titleWrapper } from './SetupComputeModal.styles';
import { billingPlansWrapper, billingPlanWrapper, enterprisePlanWrapper, selectPlanWrapper } from './RestrictBillingPlan.styles';

type RestrictBillingPlanProps = {
  children: React.ReactNode;
  closeAction: () => void;
  requiredScopes?: ScopeMatcher[];
  requiredFeaturedFlags?: FeaturedFlagsUnion[];
};

type UpgradePlanWrapperProps = {
  children?: React.ReactNode;
  requiredScopes?: ScopeMatcher[];
  requiredFeaturedFlags?: FeaturedFlagsUnion[];
  redirectToBillingPage?: boolean;
  upgradeCtaLabel?: string;
  upgradeCtaTrackEvent?: string;
};

type BillingPlanProps = {
  plan: Plan;
  selectPlanAndUpgrade: (planId: Plan['id']) => void;
  selectedPlanId?: string;
  isBtnLoading?: boolean;
  isBtnDisabled?: boolean;
};

export type RestrictBillingPlanContextType = {
  requiredScopes?: ScopeMatcher[];
  requiredFeaturedFlags?: FeaturedFlagsUnion[];
  startUpgradePlanFlow: () => void;
  endUpgradePlanFlow: () => void;
};

const RestrictBillingPlanContext = createContext<RestrictBillingPlanContextType>({
  startUpgradePlanFlow: noop,
  endUpgradePlanFlow: noop,
});

function useRestrictBillingPlan() {
  return useContext(RestrictBillingPlanContext);
}

export function RestrictBillingPlan(props: RestrictBillingPlanProps) {
  const [upgradePlanFlow, setUpgradePlanFlow] = useState(false);

  return (
    <RestrictBillingPlanContext.Provider
      value={{
        requiredScopes: props.requiredScopes,
        requiredFeaturedFlags: props.requiredFeaturedFlags,
        startUpgradePlanFlow: () => setUpgradePlanFlow(true),
        endUpgradePlanFlow: () => setUpgradePlanFlow(false),
      }}
    >
      {upgradePlanFlow ? <SelectPlan closeAction={props.closeAction} /> : props.children}
    </RestrictBillingPlanContext.Provider>
  );
}

export function UpgradePlanWrapper({
  children,
  requiredFeaturedFlags: defaultRequiredFeaturedFlags = [],
  requiredScopes: defaultRequiredScopes = [],
  redirectToBillingPage = false,
  upgradeCtaLabel = 'Upgrade your plan',
  upgradeCtaTrackEvent,
}: UpgradePlanWrapperProps) {
  const userId = useCurrentUserId();
  const router = useRouter();
  const { track } = useDataCollection();
  const {
    startUpgradePlanFlow,
    requiredFeaturedFlags = defaultRequiredFeaturedFlags,
    requiredScopes = defaultRequiredScopes,
  } = useRestrictBillingPlan();
  const { openLoginModal } = useAuthActions();
  const { data: userScopesE, isFetching } = useUserLevelScopes();
  const hasRequiredFeaturedFlagsArr = useFeaturedFlags(requiredFeaturedFlags);

  const handleUpgradePlanClick = () => {
    if (redirectToBillingPage) {
      if (upgradeCtaTrackEvent) {
        track(upgradeCtaTrackEvent);
      }
      router.push(`/${userId}/settings/billing`);
    } else {
      startUpgradePlanFlow?.();
    }
  };

  const hasRequiredScopes = checkIfHasAllScopes(requiredScopes, userScopesE);
  const hasRequiredFeaturedFlags = hasRequiredFeaturedFlagsArr.every(Boolean);
  const hasAccess = hasRequiredScopes && hasRequiredFeaturedFlags;

  if (!userId) {
    return (
      <Button size="md" onClick={() => openLoginModal(AuthModalState.SHOW_SIGN_IN)} loading={isFetching}>
        Login
      </Button>
    );
  }

  if (!hasAccess) {
    return (
      <Button size="md" onClick={handleUpgradePlanClick} loading={isFetching}>
        {upgradeCtaLabel}
      </Button>
    );
  }

  return <>{children}</>;
}

// Some code copied from: modules/User/components/Settings/Billing/CreditCard/AddNewCard.tsx
function SelectPlan({ closeAction }: { closeAction: () => void }) {
  const { track } = useDataCollection();
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>();
  const [showAddCreditCard, setShowAddCreditCard] = useBoolean(false);
  const { endUpgradePlanFlow } = useRestrictBillingPlan();
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  const { billingPlans } = useFeatureFlagsLD();
  const BillingPlansData = billingPlans as TBillingPlansDataLD;

  const EnterprisePlanIcon = BillingPlanIconMapping[ENTERPRISE_PLAN_ID];

  const { data: creditCards, isFetching: isCreditCardsFetching } = useQueryTE(
    [queryKeys.UserCreditCards, { userId }],
    getCreditCardsTE({ userId }, errorToReactLeft),
  );

  // NOTE: Check the logic to make sure its fully correct
  const hasAnyValidCreditCard = pipe(
    creditCards,
    either.map((resp) =>
      resp?.cards?.some((card) => {
        const expiryDay = new Date();
        expiryDay.setFullYear(Number.parseInt(card.exp_year, 10), Number.parseInt(card.exp_month, 10), 1);
        return expiryDay > new Date();
      }),
    ),
    either.getOrElse(() => false),
  );

  const upgradePlanMutation = useUserSubscriptionMutation({
    userId,
    onSuccess: (resp) => {
      queryClient.invalidateQueries([queryKeys.UserBilling, { userId }]);

      const respPlan = BillingPlansData?.plans.find((plan) => plan.id === resp.plan.id);
      cogoToast.success(`Plan upgraded to ${respPlan?.name}.`);
      track('Home Page -> Subscription Plan Updated', { plan: respPlan });
      endUpgradePlanFlow();
    },
    onError: (error) => {
      track('Home Page -> Subscription Plan Upgrade Error', { message: error.props?.reason });
      cogoToast.error(error.props?.reason, { heading: 'Error' });
    },
  });

  const handleUpgradePlan = useCallback(
    (planId?: string) => {
      const upgradePlanTo = planId || selectedPlanId;
      if (!upgradePlanTo) {
        return;
      }
      upgradePlanMutation.mutate({ plan: { id: upgradePlanTo } });
    },
    [selectedPlanId],
  );

  const addCardMutation = useMutationTE((card: CreditCardPayload) => addCreditCardTE({ userId, card }, errorToReactLeft)(), {
    onSuccess: () => {
      track('Home Page -> Card Added Successfully');
      cogoToast.success('Card added successfully');
      setShowAddCreditCard(false);
      handleUpgradePlan();
      queryClient.invalidateQueries([queryKeys.UserCreditCards, { userId }]);
    },
    onError: (error) => {
      track('Home Page -> Card Adding Error', { message: error.props?.reason });
      cogoToast.error(error.props?.reason, { heading: error.props?.title });
    },
  });

  const handleAddNewCard = (card: CreditCardFormSubmitCallback): void => {
    addCardMutation.mutate({
      name: card.name,
      number: card.number,
      exp_month: card.exp_month,
      exp_year: card.exp_year,
      cvc: card.cvc,
      address_line_1: card.address_line_1,
      address_line_2: card.address_line_2,
      address_city: card.address_city,
      address_state: card.address_state,
      address_zip: card.address_zip,
      address_country: card.address_country,
      default: true, // as of now we have to set the new card as default as the form doesn't have a checkbox to set the default card
    });
  };

  const selectPlanAndUpgrade = useCallback(
    (planId: string) => {
      setSelectedPlanId(planId);

      if (!hasAnyValidCreditCard) {
        setShowAddCreditCard(true);
        return;
      }

      handleUpgradePlan(planId);
    },
    [hasAnyValidCreditCard],
  );

  if (showAddCreditCard) {
    return <CreditCardModal onClose={closeAction} onSubmit={handleAddNewCard} />;
  }

  return (
    <div className={selectPlanWrapper}>
      <div className={titleWrapper}>
        <h3 className={titleStyles}>Upgrade to get access to dedicated compute</h3>
        <dialogComponents.CloseButton onClick={closeAction} />
      </div>

      <div style={{ position: 'relative', minHeight: 100, minWidth: 'min(100%, 600px)' }}>
        <LoadingOverlay visible={!BillingPlansData} loader={<Loader variant="dots" />} />
        <div className={billingPlansWrapper}>
          {BillingPlansData?.plans.map((plan) => (
            <BillingPlan
              key={`select-billing-plan${plan.id}`}
              plan={plan}
              selectedPlanId={selectedPlanId}
              selectPlanAndUpgrade={selectPlanAndUpgrade}
              isBtnLoading={upgradePlanMutation.isLoading && selectedPlanId === plan.id}
              isBtnDisabled={isCreditCardsFetching || upgradePlanMutation.isLoading}
            />
          ))}
        </div>
      </div>

      {BillingPlansData && 'enterprisePlan' in BillingPlansData && (
        <div className={enterprisePlanWrapper}>
          <EnterprisePlanIcon size={24} style={{ minWidth: 24 }} />

          <p>
            <FormatText text={BillingPlansData.enterprisePlan?.description} />
          </p>

          <div className="buttons-wrapper">
            {BillingPlansData.enterprisePlan.links.map((link, idx) => (
              <a key={link.label + idx} href={link.url} className={cx('contact-btn', !idx && 'tertiary')} target="_blank" rel="noreferrer">
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BillingPlan({ plan, selectedPlanId, selectPlanAndUpgrade, isBtnLoading, isBtnDisabled }: BillingPlanProps) {
  const userId = useCurrentUserId();
  const { data: userSubscriptionE } = useQueryTE([queryKeys.UserBilling, { userId }], billingApiEffects.getUserSubscriptionTE(userId), {
    enabled: !!userId,
    staleTime: 1000 * 60,
  });

  const userPlanId = pipe(
    userSubscriptionE,
    either.map((userPlan) => userPlan.id),
    either.getOrElse(() => ''),
  );

  const Icon = BillingPlanIconMapping[plan.id as keyof typeof BillingPlanIconMapping];
  const isDisabled = isBtnDisabled || plan.id === userPlanId;

  return (
    <div key={`upgrade-plan-to-${plan.id}`} className={cx(billingPlanWrapper, selectedPlanId === plan.id && 'selected', isDisabled && 'disabled')}>
      <div>
        <div className="plan-title-wrapper">
          {Icon && <Icon size={24} />}
          <h4 className="title">{plan.name}</h4>
        </div>

        <p>
          <FormatText text={plan.description} />
        </p>

        <ul className="features-list">
          {plan.featuresListHeading && <li className="preceding-plan">{plan.featuresListHeading}</li>}

          {plan.features.map((feature) => (
            <li key={JSON.stringify(feature)} className="feature">
              <IconCheckThin size={20} />
              <p>
                <FormatText text={feature} />
              </p>
            </li>
          ))}

          {plan.moreFeaturesLink && (
            <li>
              <a href={plan.moreFeaturesLink.url} className="more-link-wrapper" target="_blank" rel="noreferrer">
                <span className="more-link">{plan.moreFeaturesLink.label}</span>
                <IconLinkExternal size={15} />
              </a>
            </li>
          )}
        </ul>
      </div>
      <div className="footer">
        <div className="price-wrapper">
          <span className="price-label">Starts at</span>
          <strong className="price">
            ${plan.dollarPricePerMonth}
            /mo
          </strong>
        </div>
        <Button size="md" disabled={isDisabled} loading={isBtnLoading} onClick={() => selectPlanAndUpgrade(plan.id)}>
          Upgrade to {plan.name}
        </Button>
      </div>
    </div>
  );
}

function FormatText({ text }: { text: Text }) {
  return (
    <>
      {Array.isArray(text)
        ? text.map((item) =>
            typeof item === 'string' ? item : item.type === 'bold' ? <strong key={`${item.text}-bold`}>{item.text}</strong> : item.text,
          )
        : text}
    </>
  );
}
